import React, { useEffect, useState } from 'react';
import BitrixAPI_B24 from '../api/BitrixAPI_B24';
import { API_URL } from '../config';

const OrderFormPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      script.async = false;
      document.head.appendChild(script);
    });
  };

  const loadCSS = (href) => {
    const existing = document.querySelector(`link[href="${href}"]`);
    if (existing) return;
    
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  };

  // Функция для проверки и отладки изображений
  const checkImageAssets = () => {
    console.log('Проверяем доступность изображений...');
    console.log('CONFIG.assetsBase должен быть:', '/pageb2b/assets/');
    
    // Проверяем несколько ключевых изображений
    const testImages = [
      '/pageb2b/assets/images/categories/dush/formy/pryamaya.png',
      '/pageb2b/assets/images/steklo/cvet/prozrachny.png',
      '/pageb2b/assets/images/steklo/obrabotka/bez-matovoy-obrabotki.png',
      '/pageb2b/assets/images/furnitura/cvet/khrom.png'
    ];
    
    testImages.forEach(imagePath => {
      const img = new Image();
      img.onload = () => console.log(`✅ Изображение доступно: ${imagePath}`);
      img.onerror = () => console.log(`❌ Изображение недоступно: ${imagePath}`);
      img.src = imagePath;
    });

    // Проверяем CONFIG из vanilla-app если он загружен
    setTimeout(() => {
      if (window.CONFIG) {
        console.log('CONFIG.assetsBase из vanilla-app:', window.CONFIG.assetsBase);
      }
    }, 500);
  };

  const setupBitrixIntegration = () => {
    if (!window.saveToLS) return;
    
    const originalSaveToLS = window.saveToLS;
    
    window.saveToLS = async (orders) => {
      if (originalSaveToLS) {
        originalSaveToLS(orders);
      }

      const lastOrder = orders[orders.length - 1];
      if (lastOrder && !lastOrder.sentToBitrix) {
        try {
          // Get authenticated user data
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const userPhone = await getUserPhone(user.id);
          
          // Debug: логируем исходные данные заказа
          console.log('=== CLIENT SIDE DEBUG ===');
          console.log('Raw lastOrder data:', lastOrder);
          console.log('User data:', user);
          console.log('User phone:', userPhone);
          
          // Дополнительное логирование для заказов стекла
          if (lastOrder.type === 'Стекло' && lastOrder.glassItems) {
            console.log('Glass items count:', lastOrder.glassItems.length);
            lastOrder.glassItems.forEach((item, index) => {
              console.log(`Glass item ${index + 1}:`, item);
              if (item.dimensions) {
                console.log(`  Dimensions for item ${index + 1}:`, item.dimensions);
                item.dimensions.forEach((dim, dimIndex) => {
                  console.log(`    Dimension ${dimIndex + 1}:`, dim);
                });
              }
            });
          }
          
          const dealData = {
            title: `${lastOrder.type} | ${user.username || 'Пользователь'}`,
            clientName: user.username || 'Не указано',
            phone: userPhone || user.phone || '',
            address: '', // No address needed
            comment: lastOrder.comment || '',
            orderType: lastOrder.type,
            system: lastOrder.system,
            area: lastOrder.area,
            totalPrice: lastOrder.glassPriceTotal || 0,
            orderDetails: formatOrderDetails(lastOrder)
          };

          console.log('Prepared dealData:', dealData);
          console.log('Formatted order details:', dealData.orderDetails);

          // Собираем файлы из глобальной переменной
          const files = window.currentOrderFiles || [];
          
          console.log('=== FILES DEBUG ===');
          console.log('window.currentOrderFiles:', window.currentOrderFiles);
          console.log('Files to upload:', files.length);
          files.forEach((file, index) => {
            console.log(`File ${index + 1}:`, {
              name: file.name,
              size: file.size,
              type: file.type,
              lastModified: file.lastModified,
              isFile: file instanceof File
            });
          });
          
          await BitrixAPI_B24.createDeal(dealData, files);
          
          // Очищаем файлы после отправки
          window.currentOrderFiles = null;
          
          lastOrder.sentToBitrix = true;
          lastOrder.bitrixSentAt = new Date().toISOString();
          
          if (originalSaveToLS) {
            originalSaveToLS(orders);
          }
          
          showNotification('Заказ успешно отправлен в Bitrix24 как сделка!', 'success');
        } catch (error) {
          console.error('Failed to send order to Bitrix24:', error);
          showNotification('Ошибка отправки в Bitrix24: ' + error.message, 'error');
        }
      }
    };
  };

  // Function to get user's phone number
  const getUserPhone = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !userId) return null;
      
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        return userData.phone;
      }
    } catch (error) {
      console.error('Error fetching user phone:', error);
    }
    return null;
  };

  // Функция для получения путей к изображениям из конфигурации vanilla-app
  const getImagePath = (category, value) => {
    const baseUrl = '/pageb2b/assets/'; // Правильный путь от корня сайта
    
    // Маппинг изображений из vanilla-app.js CONFIG
    const imageMap = {
      // Формы душевых перегородок
      forms: {
        "Прямая": "images/categories/dush/formy/pryamaya.png",
        "Угловая": "images/categories/dush/formy/uglovaya.png",
        "Трапеция": "images/categories/dush/formy/trapetsiya.png",
        "П образная": "images/categories/dush/formy/p-obraznaya.png",
        "Т образная": "images/categories/dush/formy/t-obraznaya.png",
        "TI образная": "images/categories/dush/formy/ti-obraznaya.png",
        "ТТ образная": "images/categories/dush/formy/tt-obraznaya.png",
        "ТТI образная": "images/categories/dush/formy/tti-obraznaya.png",
        "Душевой стационар": "images/categories/dush/formy/fixed-shower.png",
        "Ванная стационарная шторка": "images/categories/dush/formy/bath-fixed.png",
        "Ванная навесная шторка": "images/categories/dush/formy/bath-swing.png",
      },
      // Цвета стекла
      glassColors: {
        "Прозрачный": "images/steklo/cvet/prozrachny.png",
        "Бронза": "images/steklo/cvet/bronza.png", 
        "Графит": "images/steklo/cvet/grafit.png",
      },
      // Рифленое стекло
      ribbedGlassColors: {
        "Прозрачный": "images/steklo/cvet/riflennoe-prozrachnoe.png",
        "Бронза": "images/steklo/cvet/riflennoe-bronza.png",
        "Графит": "images/steklo/cvet/riflennoe-grafit.png",
      },
      // Обработка стекла
      processing: {
        "Без матовой обработки": "images/steklo/obrabotka/bez-matovoy-obrabotki.png",
        "Матовая обработка": "images/steklo/obrabotka/matovaya-obrabotka.png",
        "Комби-матовая обработка": "images/steklo/obrabotka/kombi-matovaya-obrabotka.png",
      },
      // Цвета фурнитуры
      hardwareColors: {
        "Хром": "images/furnitura/cvet/khrom.png",
        "Чёрный": "images/furnitura/cvet/cherny.png", 
        "Бронза": "images/furnitura/cvet/bronza.png",
        "Золото": "images/furnitura/cvet/zoloto.png",
      },
      // LOFT цвета профилей
      loftProfileColors: {
        "Чёрный": "images/furnitura/cvet/cherny.png",
        "Белый": "images/furnitura/cvet/bely.png",
        "Бронза": "images/furnitura/cvet/bronza.png", 
        "Шампань": "images/furnitura/cvet/shampan.png",
        "Золото": "images/furnitura/cvet/zoloto.png",
      },
      // LOFT профили ручек
      loftHandleProfiles: {
        "Бабочка симмтерия": "images/categories/loft/profiles/babochka-simmetriya.png",
        "Квадратно широкий усиленный": "images/categories/loft/profiles/kvadratno-shiroky-usilenny.png",
      }
    };

    const imagePath = imageMap[category]?.[value];
    return imagePath ? baseUrl + imagePath : null;
  };

  const formatOrderDetails = (order) => {
    let details = `Тип заказа: ${order.type}\n`;
    
    if (order.type === 'Душевая перегородка') {
      details += `Система: ${order.system || 'Не указана'}\n`;
      details += `Форма: ${order.form || 'Не указана'}\n`;
      details += `Стекло: ${order.glass || 'Не указано'}${order.glassColorName ? ' — ' + order.glassColorName : ''}\n`;
      details += `Обработка: ${order.processingName || 'Не указана'}\n`;
      
      // Фурнитура
      if (order.hardware) {
        details += `Фурнитура: ${order.hardware.include ? (order.hardware.colorName || 'С фурнитурой') : 'Без фурнитуры'}\n`;
      } else {
        details += `Фурнитура: Без фурнитуры\n`;
      }
      
      // Сверление
      if (order.hardware && order.hardware.include) {
        if (order.hardware.hole) {
          details += `Сверление: ${order.hardware.hole}\n`;
        }
      } else if (order.hardware && order.hardware.freeChoice) {
        details += `Сверление: ${order.hardware.freeChoice}\n`;
      }
      
      // Усиление  
      if (order.reinforcementName) {
        details += `Усиление: ${order.reinforcementName}\n`;
      } else if (order.reinforcement && order.reinforcement.type) {
        const reinforcementNames = {
          'none': 'Без усиления',
          'r45': 'Усиление 45 градусов', 
          'r90': 'Усиление 90 градусов',
          'r180': 'Усиление 180 градусов'
        };
        details += `Усиление: ${reinforcementNames[order.reinforcement.type] || order.reinforcement.type}\n`;
      }
      
      // Размеры
      if (order.dims && typeof order.dims === 'object') {
        const sizeStr = Object.entries(order.dims)
          .filter(([key, value]) => value && value.toString().trim())
          .map(([key, value]) => `${key}: ${value}${typeof value === 'number' || /^\d+$/.test(value) ? ' мм' : ''}`)
          .join(', ');
        if (sizeStr) {
          details += `Размеры: ${sizeStr}\n`;
        }
      }
      
      // Файлы
      details += `Файлы: ${order.fileCount || 0}\n`;
      if (order.mediaFiles && order.mediaFiles.length > 0) {
        order.mediaFiles.forEach((file, index) => {
          const fileSize = file.size ? ` (${Math.round(file.size / 1024)} КБ)` : '';
          details += `  ${index + 1}. ${file.name}${fileSize}\n`;
        });
      }
      
      details += `Квадратура: ${order.area || 0} м²\n`;
      details += `Цена стекла: ${order.glassPriceTotal || 0} ₸\n`;
      
    } else if (order.type === 'LOFT перегородка') {
      details += `Система: ${order.system || 'Не указана'}\n`;
      details += `Количество дверей: ${order.doorCount || 'Не указано'}\n`;
      details += `Раскладка дверей: ${order.doorLayout || 'Не указана'}\n`;
      details += `Стекло: ${order.glass || 'Не указано'}\n`;
      details += `Тип стекла: ${order.glassType || 'Не указан'}\n`;
      
      details += `Цвет стекла: ${order.glassColorName || 'Не указан'}\n`;
      details += `Обработка: ${order.processingName || 'Не указана'}\n`;
      details += `Цвет профиля: ${order.profileColorName || 'Не указан'}\n`;
      details += `Профиль ручки: ${order.handleProfileName || 'Не указан'}\n`;
      
      // Размеры
      if (order.dims && typeof order.dims === 'object') {
        details += `Высота (H): ${order.dims.H || 'Не указана'}\n`;
        details += `Длина (L): ${order.dims.L || 'Не указана'}\n`;
        details += `Рельс: ${order.dims.rail || 'Не указан'}\n`;
      }
      
      details += `Площадь: ${order.area || 0} м²\n`;
      details += `Стоимость: ${order.glassPriceTotal || 0} ₸\n`;
      
      // Файлы
      details += `Файлы: ${order.fileCount || 0}\n`;
      if (order.mediaFiles && order.mediaFiles.length > 0) {
        order.mediaFiles.forEach((file, index) => {
          const fileSize = file.size ? ` (${Math.round(file.size / 1024)} КБ)` : '';
          details += `  ${index + 1}. ${file.name}${fileSize}\n`;
        });
      }
      
    } else if (order.type === 'Стекло') {
      details += `Количество позиций: ${order.glassItems ? order.glassItems.length : 0}\n`;
      
      // Детали по каждой позиции стекла
      if (order.glassItems && order.glassItems.length > 0) {
        order.glassItems.forEach((item, index) => {
          details += `Позиция ${index + 1}: ${item.name || 'Без названия'}\n`;
          details += `Обработка: ${item.processing === 'raw' ? 'Сырое стекло' : 'Каленое стекло'}\n`;
          details += `Тип: ${item.glassType === 'regular' ? 'Обычное стекло' : 'Рифленое стекло'}\n`;
          details += `Толщина: ${item.thickness || 'Не указана'}\n`;
          details += `Цвет: ${item.glassColorName || 'Не указан'}\n`;
          
          if (item.matteProcessing && item.glassType === 'regular') {
            details += `Матовая обработка: ${item.matteProcessing}\n`;
          }
          
          // Размеры для этой позиции
          if (item.dimensions && item.dimensions.length > 0) {
            details += `Габариты:\n`;
            item.dimensions.forEach((dim, dimIndex) => {
              details += `${dim.width} × ${dim.height} мм — ${dim.quantity} шт.\n`;
              if (dim.holes && dim.holes > 0) details += `Отверстия: ${dim.holes}\n`;
              if (dim.euroEdge) details += `Еврокромка: да\n`;
            });
          } else {
            details += `Габариты: не указаны\n`;
          }
          
          details += `Стоимость: ${item.totalPrice || 0} ₸\n`;
        });
      }
      
      details += `Общая квадратура: ${(order.area || 0).toFixed(2)} м²\n`;
      details += `Общая стоимость: ${order.glassPriceTotal || 0} ₸\n`;
      
      // Файлы
      details += `Файлы: ${order.fileCount || 0}\n`;
      if (order.mediaFiles && order.mediaFiles.length > 0) {
        order.mediaFiles.forEach((file, index) => {
          const fileSize = file.size ? ` (${Math.round(file.size / 1024)} КБ)` : '';
          details += `  ${index + 1}. ${file.name}${fileSize}\n`;
        });
      }
    }
    
    if (order.comment) {
      details += `\nКомментарий: ${order.comment}\n`;
    }
    
    return details;
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 8px;
      color: white;
      z-index: 10000;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      max-width: 400px;
      word-wrap: break-word;
      background-color: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6'};
    `;
    
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  };

  const checkScriptsLoaded = () => {
    const hasVanApp = !!window.VanApp;
    const hasPricing = !!window.PRICING;
    
    // CONFIG не экспортируется в window, проверим через VanApp
    const hasVanAppInit = !!(window.VanApp && typeof window.VanApp.initFormPage === 'function');
    
    console.log('Script check:', { 
      VanApp: hasVanApp,
      VanAppInit: hasVanAppInit,
      PRICING: hasPricing,
      vanAppKeys: window.VanApp ? Object.keys(window.VanApp) : [],
      windowKeys: Object.keys(window).filter(k => k.includes('Van') || k.includes('PRICING') || k.includes('CONFIG'))
    });
    
    return hasVanApp && hasPricing && hasVanAppInit;
  };

  useEffect(() => {
    // Если скрипты уже загружены, сразу инициализируем контент
    if (checkScriptsLoaded()) {
      console.log('Scripts already loaded, setting content immediately');
      const content = `
        <div class="wrap">
          <div class="hdr">
            <h1>Форма заказа B2B</h1>
            <div class="flex">
              <span class="small">v4 • интеграция с Bitrix24</span>
            </div>
          </div>
          <div id="clientMount"></div>
          <div id="calcMount"></div>
          <div id="showerCard" class="card hidden">
            <div class="page-title">
              <h2>Регистрация - душевой перегородки</h2>
              <div class="flex" id="orderTypeBtns"></div>
            </div>
            <div id="form-shower" class="section"></div>
          </div>
          <div id="loftCard" class="card hidden">
            <div class="page-title">
              <h2>Регистрация - LOFT перегородки</h2>
              <div class="flex" id="orderTypeBtnsLoft"></div>
            </div>
            <div id="form-loft" class="section"></div>
          </div>
          <div id="glassCard" class="card hidden">
            <div class="page-title">
              <h2>Регистрация - стекла</h2>
              <div class="flex" id="orderTypeBtnsGlass"></div>
            </div>
            <div id="form-glass" class="section"></div>
          </div>
          <div id="savedMount"></div>
          <p class="small">Заказы отправляются в Bitrix24 как сделки.</p>
        </div>
      `;
      
      setHtmlContent(content);
      setIsLoaded(true);
      return;
    }

    const initApp = async () => {
      try {
        console.log('Loading scripts for order form...');
        loadCSS('/pageb2b/styles.css');
        
        await loadScript('/pageb2b/pricing.js');
        console.log('Pricing.js loaded, PRICING available:', !!window.PRICING);
        
        // Дополнительная проверка после загрузки pricing.js
        let pricingAttempts = 0;
        while (!window.PRICING && pricingAttempts < 5) {
          await new Promise(resolve => setTimeout(resolve, 100));
          pricingAttempts++;
        }
        console.log('PRICING final check:', !!window.PRICING);
        
        await loadScript('/pageb2b/vanilla-app.js');
        console.log('Vanilla-app.js loaded, VanApp available:', !!window.VanApp, 'CONFIG available:', !!window.CONFIG);
        
        // Дополнительная проверка после загрузки vanilla-app.js
        let vanAppAttempts = 0;
        while ((!window.VanApp || typeof window.VanApp.initFormPage !== 'function') && vanAppAttempts < 5) {
          await new Promise(resolve => setTimeout(resolve, 100));
          vanAppAttempts++;
        }
        console.log('VanApp final check:', !!window.VanApp, !!(window.VanApp && window.VanApp.initFormPage));
        
        // Дополнительная проверка загрузки с повторными попытками
        let attempts = 0;
        const maxAttempts = 10;
        
        while (!checkScriptsLoaded() && attempts < maxAttempts) {
          console.log(`Waiting for scripts to initialize... Attempt ${attempts + 1}/${maxAttempts}`);
          await new Promise(resolve => setTimeout(resolve, 200));
          attempts++;
        }
        
        if (!checkScriptsLoaded()) {
          const missingScripts = [];
          if (!window.VanApp) missingScripts.push('VanApp');
          if (!window.PRICING) missingScripts.push('PRICING');
          if (!(window.VanApp && window.VanApp.initFormPage)) missingScripts.push('VanApp.initFormPage');
          
          throw new Error(`Scripts failed to load: ${missingScripts.join(', ')}. VanApp keys: ${window.VanApp ? Object.keys(window.VanApp).join(', ') : 'none'}`);
        }
        
        console.log('All scripts loaded successfully, creating content...');
        
        const content = `
          <div class="wrap">
            <div class="hdr">
              <h1>Форма заказа B2B</h1>
              <div class="flex">
                <span class="small">v4 • интеграция с Bitrix24</span>
              </div>
            </div>
            <div id="clientMount"></div>
            <div id="calcMount"></div>
            <div id="showerCard" class="card hidden">
              <div class="page-title">
                <h2>Регистрация - душевой перегородки</h2>
                <div class="flex" id="orderTypeBtns"></div>
              </div>
              <div id="form-shower" class="section"></div>
            </div>
            <div id="loftCard" class="card hidden">
              <div class="page-title">
                <h2>Регистрация - LOFT перегородки</h2>
                <div class="flex" id="orderTypeBtnsLoft"></div>
              </div>
              <div id="form-loft" class="section"></div>
            </div>
            <div id="glassCard" class="card hidden">
              <div class="page-title">
                <h2>Регистрация - стекла</h2>
                <div class="flex" id="orderTypeBtnsGlass"></div>
              </div>
              <div id="form-glass" class="section"></div>
            </div>
            <div id="savedMount"></div>
            <p class="small">Заказы отправляются в Bitrix24.</p>
          </div>
        `;
        
        setHtmlContent(content);
        setIsLoaded(false); // Будет установлен в true после полной инициализации
        console.log('Content set successfully');
      } catch (error) {
        console.error('Error loading app:', error);
        setError('Ошибка загрузки формы заказа: ' + error.message);
      }
    };

    initApp();
  }, []);

  const setupUserDataOverride = async () => {
    // Get user data first
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userPhone = await getUserPhone(user.id);
    
    // Override the client card rendering to use authenticated user data
    const originalRenderClientCard = window.renderClientCard;
    
    window.renderClientCard = () => {
      const mount = document.getElementById("clientMount") || document.querySelector(".wrap");
      if (!mount) return;
      
      mount.innerHTML = "";
      
      // Create a simple display card instead of input form
      const card = document.createElement('div');
      card.className = 'card';
      card.innerHTML = `
        <div class="page-title">
          <h2>Данные пользователя</h2>
        </div>
        <div class="row">
          <div style="padding: 10px; background: #f9f9f9; border-radius: 5px; margin: 10px 0;">
            <p><strong>Пользователь:</strong> ${user.username || 'Не указан'}</p>
            <p><strong>Телефон:</strong> ${userPhone || 'Не указан'}</p>
          </div>
        </div>
      `;
      
      mount.appendChild(card);
      
      // Override ClientFields with user data
      window.ClientFields = {
        nameInp: { value: user.username || '' },
        phoneInp: { value: userPhone || '' },
        addressInp: { value: '' }
      };
    };
    
    // Call the new render function immediately if it exists
    if (window.renderClientCard) {
      window.renderClientCard();
    }
  };

  useEffect(() => {
    if (htmlContent && checkScriptsLoaded()) {
      console.log('HTML content ready and scripts loaded, initializing form...');
      
      const initializeForm = async () => {
        try {
          // Проверяем, что все необходимые функции доступны
          if (!window.VanApp?.initFormPage) {
            console.error('VanApp.initFormPage not available');
            return;
          }
          
          console.log('Initializing VanApp form page...');
          window.VanApp.initFormPage();
          
          console.log('Setting up Bitrix integration...');
          setupBitrixIntegration();
          
          console.log('Setting up user data override...');
          await setupUserDataOverride();
          
          console.log('Checking image assets...');
          checkImageAssets();
          
          console.log('Form initialization completed successfully');
          setIsLoaded(true);
        } catch (error) {
          console.error('Error initializing form:', error);
          // Пробуем повторить инициализацию через 500ms
          setTimeout(initializeForm, 500);
        }
      };

      const timer = setTimeout(initializeForm, 100);
      return () => clearTimeout(timer);
    } else {
      console.log('Waiting for content and scripts...', {
        htmlContent: !!htmlContent,
        scriptsLoaded: checkScriptsLoaded()
      });
    }
  }, [htmlContent]);

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '0', margin: '0' }}>
      {!htmlContent && (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', marginBottom: '20px' }}>
            Загрузка формы заказа...
          </div>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      {htmlContent && !isLoaded && (
        <div style={{ 
          position: 'fixed', 
          top: '10px', 
          right: '10px', 
          background: '#fff', 
          padding: '10px 20px', 
          borderRadius: '5px', 
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          Инициализация формы...
        </div>
      )}
      {htmlContent && (
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      )}
    </div>
  );
};

export default OrderFormPage;